import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useCallback, useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { VirtualizedTableData, VirtualizedTime } from '../Common/VirtualizedTableComponents';
import { RunStatus, Time } from '../../svgUrls';
import StatusChip from '../Common/StatusChip';
import { RunInstanceNameToColor } from '../../constants/workflow';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { ViewsDataType } from './DataViews';
import { FallbackSvg } from '../Common/FallbackSvg';
import { WorkflowServiceIcon } from '../Common/WorkflowServiceIcon';
type DataViewsTableProps = {
  viewsData: ViewsDataType[];
  columnOrder?: string[];
  columnVisibility?: Record<string, boolean>;
  allColumns: string[];
  sortKey: string;
  sortOrder: 'asc' | 'desc';
  onSortKeyChange: (_sortKey: string, _sortOrder: "asc" | "desc") => void;
};

const DataViewsTable = ({
  viewsData,
  columnOrder,
  columnVisibility,
  allColumns,
  sortKey,
  sortOrder,
  onSortKeyChange,
}: DataViewsTableProps) => {
    const columnHelper = createColumnHelper<ViewsDataType>();

    const setSort = useCallback((column: string) => {
      onSortKeyChange(column, column === sortKey && sortOrder === 'asc' ? 'desc' : 'asc');
    }, [onSortKeyChange, sortKey, sortOrder]);
  
    const fieldsData = useMemo(
      () =>
        allColumns?.map((column) => {
          const iconName = column.split("|").pop();
          const columnName = column.split('|').slice(0, -2).join("|");
          return columnHelper.accessor((row) => row.data, {
            id: column,
            header: () => {
                let isSortColumn = sortKey === column;
                let isAsc = sortOrder === 'asc';
                return <div className='flex gap-2 items-center cursor-pointer' onClick={() => setSort(column)}>
                    {
                        iconName === "none" && columnName === "Run status" ?
                            <RunStatus />
                        : iconName === "none" && columnName === "Last step run" ?
                            <Time />
                            : <WorkflowServiceIcon
                                workflowType={iconName || ''}
                                alt={iconName || ''}
                                height={16}
                                width={16}
                            />
                    }
                    {columnName.replace('$.', '')}
                    {
                      isSortColumn ?
                        isAsc ?
                          <KeyboardArrowUp className='!text-base' />
                          : <KeyboardArrowDown className='!text-base' />
                        : null
                    }
                </div>
            },
            cell: (info) => (
                columnName.toLowerCase().includes('time') ?
                    <VirtualizedTime time={info.getValue()?.[columnName] || ''} />
                    : columnName === "Run status" ?
                        <StatusChip
                            statusText={info.getValue()?.[columnName]}
                            themeColor={RunInstanceNameToColor[info.getValue()?.[columnName]]}
                            showBorder
                            additionalClasses="font-medium"
                        />
                    : <VirtualizedTableData value={info.getValue()?.[columnName] || ''} />
            ),
          })
        }),
      [allColumns, columnHelper, setSort, sortKey, sortOrder]
    );
  
    const columns = useMemo(
      () => [
        // columnHelper.accessor((row) => row, {
        //   id: 'select',
        //   header: ({ table }) => (
        //     <div className="flex justify-center">
        //       <Checkbox
        //         color="primary"
        //         size="small"
                
        //         checked={table.getIsAllRowsSelected()}
        //         indeterminate={table.getIsSomeRowsSelected()}
        //         onChange={table.getToggleAllRowsSelectedHandler()}
        //         className='!text-[#7E86A0]'
        //         aria-label="Select all rows"
        //       />
        //     </div>
        //   ),
        //   cell: ({ row }) => (
        //     <div className="flex justify-center">
        //       <Checkbox
        //         color="primary"
        //         size="small"
        //         checked={row.getIsSelected()}
        //         onChange={row.getToggleSelectedHandler()}
        //         onClick={(e) => e.stopPropagation()}
        //         className="checkbox-row !text-[#7E86A0]"
        //         aria-label={`Select row ${row.id}`}
        //       />
        //       <div className="placeholder-div hidden flex-1"></div>
        //     </div>
        //   ),
        // }),
        ...fieldsData,
      ],
      [fieldsData]
    );
  
    const tableData = useMemo(
      () => viewsData,
    [viewsData]);
  
    const table = useReactTable<ViewsDataType>({
      columns,
      data: tableData,
      getRowId: (row) => row.id,
      state: {
        columnOrder,
        columnVisibility,
      },
      getCoreRowModel: getCoreRowModel(),
    });
  
    const { rows } = table.getRowModel();
  
    const rootRef = useRef<HTMLDivElement>(null);
    const tableContainerRef = useRef<HTMLDivElement>(null);
  
    const rowVirtualizer = useVirtualizer({
        getScrollElement: () => tableContainerRef.current,
      count: rows.length,
      estimateSize: () => 50,
    });
  
    const virtualRows = rowVirtualizer.getVirtualItems();
    const totalSize = rowVirtualizer.getTotalSize();
  
    const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
    const paddingBottom =
      virtualRows.length > 0
        ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0)
        : 0;
  
    return (
      <div ref={rootRef} className="overflow-hidden w-full h-full">
        <div
          ref={tableContainerRef}
          className="overflow-auto"
          style={{
            height: (rootRef.current?.clientHeight || 320),
          }}
        >
          <table className="w-full border-spacing-0 border-separate">
            <thead className="sticky top-0 z-20 h-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="bg-[#F8F9FA]">
                  {headerGroup.headers.map((header, index) => (
                    <th
                      key={header.id}
                      className={clsx(
                        'whitespace-nowrap border-b border-solid border-[#ECEDF1] px-4 pb-1 text-left text-xs font-medium text-greyishBlueMid first:sticky first:left-0',
                        index === 0 ? 'bg-[#F8F9FA] border-r-[2px] border-solid border-lightGrey' : ''
                      )}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {paddingTop > 0 && (
                <tr>
                  <td style={{ height: paddingTop }} />
                </tr>
              )}
              {virtualRows.map((virtualRow, rowIndex) => {
                const row = rows[virtualRow.index];
  
                return (
                    <tr
                        className="group h-[40px] cursor-pointer text-xs bg-white hover:bg-backgroundGreyLight rounded"
                        key={row.id}
                        id={row.id}
                    >
                        {row.getVisibleCells().map((cell, index) => (
                            <td
                                key={cell.id}
                                className={clsx(
                                  'border-b border-solid border-lightGrey bg-inherit px-4 text-left first:sticky first:left-0 first:bg-inherit',
                                  index === 0
                                      ? 'border-r-[2px] border-solid border-lightGrey bg-white group-hover:bg-inherit'
                                      : '',
                                )}
                            >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                            </td>
                        ))}
                    </tr>
                );
              })}
              {paddingBottom > 0 && (
                <tr>
                  <td style={{ height: paddingBottom }} />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
};

export default DataViewsTable;
  